import Singleton from './Singleton'
import {Cache, Resource} from './Cache'
import {Memory} from './Memory'
import dayjs from 'dayjs'
import {message} from 'antd'
import axios from 'axios'
import {AccountResource} from '../resources/AccountResource'
import {CommonResource} from '../resources/CommonResource'
import {EventResource} from '../resources/EventResource'
import Cookies from 'js-cookie'
import {MessagesResource} from '../resources/MessagesResource'

let localesJSON = require('../locales_cz.json')

export default class App extends Singleton {
  static API_URL = (document.location.hostname === 'localhost') ? 'http://localhost/api/' : `https://${document.location.hostname}/api/`
  static STORE = {
    is_authorized: false,
    customer_id: null,
    is_test_user: null,
    token: null,
  }
  touched_flag = false
  i18nDATA = {}

  constructor() {
    super()

    this.message = message
    this.i18nDATA = localesJSON

    axios.interceptors.response.use((response) => {

      return response.data

    }, (error) => {

      if(error.hasOwnProperty('response') && error.response.hasOwnProperty('error'))
        return Promise.reject(error.response.data)
      else if(error.hasOwnProperty('message'))
        return Promise.reject(error.message)
      else
        return Promise.reject('No internet connection')

    })

    window.$app = this
  }

  static getCustomerID() {
    return App.STORE.customer_id || null
  }

  static $toast(content, type = 'success') {
    void message.open({type: type, content: content})
  }

  static $e(content) {
    this.$toast(content, 'error')
  }

  async $api(request) {
    return new Promise(async (resolve, reject) => {

      try {
        const {action = 'default', headers = null} = request

        delete request.headers

        const response = await axios({
          withCredentials: true,
          url: `${App.API_URL}?action=${action}`,
          method: 'POST',
          headers: headers,
          data: {
            ...request
          }
        })

        /*if(response && response.hasOwnProperty('errors')){

          response.errors.forEach((message) => {
            App.$e(message.title)
          })
          reject(response.error)

        }
        if(response && response.hasOwnProperty('error')) {

          App.$e(response.error.title)
          reject(response.error)

        }*/

        if(response && response.hasOwnProperty('error')) reject(response.error)
        else resolve(response)

      } catch (error) {

        reject(error)

      }
    })
  }

  /**
   * Get resource from cache or API. Default cache time 60 minutes
   * @param name
   * @param request
   * @return {Promise<Awaited<unknown>>}
   */
  async $resource(name, request) {
    const resources = Cache.resources
    const customer_id = App.STORE.customer_id
    const resource = (resources.hasOwnProperty(name) ? new Resource(resources[name], customer_id) : false)

    if(resource instanceof Resource && resource.isValid) return Promise.resolve(resource.data.response)

    const response = await this.$api(request)

    if(response && typeof response === 'object') {

      const data = {}
      const statestamp = dayjs().unix()

      data[name] = {
        response,
        statestamp,
        customer_id
      }

      Cache.extend(data)

    }

    return Promise.resolve(response)
  }

  async $memory(name, request, force = false) {
    return Memory.get(name, request, (req) => this.$api(req), force)
  }

  /**
   * Subscribe to changes of a cached endpoint
   * @param {string} name - Cache key, e.g. personal-information
   * @param {Function} callback - Called with the fresh data, or undefined on invalidation
   * @returns {Function} Unsubscribe function
   */
  $onMemoryChange(name, callback) {
    return Memory.subscribe(name, callback)
  }

  /**
   * Drop a cached endpoint (or all of them) and notify its subscribers
   * @param {string|null} name
   */
  $forget(name = null) {
    Memory.invalidate(name)
  }

  /**
   * Get translations
   */
  $t(...keys) {
    return this.findByKeys(keys, localesJSON)
  }

  $tc(...args) {

    const count = args.pop()
    const plurals = this.findByKeys(args, localesJSON).split('|').map(item => item.trim())

    if(count === 1)
      return plurals[0]
    if(plurals.length === 3 && (4 < count || count === 0))
      return plurals[2]

    return plurals[1]
  }

  findByKeys(keys, data) {

    // Result key found
    if(keys.length === 0)
      return data

    if(data[keys[0]] !== undefined) {
      let original = keys.shift()
      return this.findByKeys(keys, data[original])
    } else {
      console.error(`JS lang key ${keys} not exist`)
      return `[JS lang key ${keys} not exist]`
    }
  }

  $e(content) {
    App.$e(content)
  }

  /**
   *
   * @returns {Promise<{is_test_user: null, is_authorized: boolean, customer_id: null}>}
   */
  async $auth() {
    if(!this.touched_flag) {
      let data = await this.$api({action: 'touch'})
      App.STORE = data
      this.touched_flag = true
    }

    return App.STORE
  }

  $store(storeName) {
    if(storeName === 'account')
      return (new AccountResource())

    else if(storeName === 'common')
      return (new CommonResource())

    else if(storeName === 'event')
      return (new EventResource())

    else if(storeName === 'messages')
      return (new MessagesResource())

    else {

      App.$e('Store not found')
      return null

    }
  }

  /**
   * Save CareCloud token as a cookie with 14-day expiry
   * @param {string} token - The token to save
   */
  saveCareCloudToken(token) {
    this.saveCareCloudTokenWithTimestamp(token, Date.now())
  }

  /**
   * Retrieve CareCloud token from cookie
   * @returns {string|null} - The token if found, null otherwise
   */
  getCareCloudToken() {
    const tokenValue = Cookies.get('_carecloud_token')

    if (!tokenValue) {
      return null
    }

    return tokenValue
  }

  /**
   * Check if token expires within 13 days and extend it if needed
   * @returns {boolean} - True if token was extended or is still valid, false if no token found
   */
  checkAndExtendCareCloudToken() {
    const token = this.getCareCloudToken()

    if (!token) {
      return false
    }

    // Get the creation timestamp from separate cookie
    const createdTimestamp = Cookies.get('_carecloud_token_expiry')

    if (!createdTimestamp) {
      // If no timestamp found, set current time and extend
      this.saveCareCloudTokenWithTimestamp(token, Date.now())
      return true
    }

    const created = parseInt(createdTimestamp, 10)

    // Check if token was created more than 1 day ago (13 days remaining)
    const oneDayInMs = 24 * 60 * 60 * 1000
    const daysSinceCreated = (Date.now() - created) / oneDayInMs

    if (daysSinceCreated >= 1) {
      // Extend the token by setting it again with fresh timestamp
      this.saveCareCloudTokenWithTimestamp(token, Date.now())
      return true
    }

    return true // Token is still fresh
  }

  /**
   * Save CareCloud token with timestamp for expiry tracking
   * @param {string} token - The token to save
   * @param {number} timestamp - Creation timestamp
   * @private
   */
  saveCareCloudTokenWithTimestamp(token, timestamp) {
    // Get the root domain by removing all subdomains
    const hostname = window.location.hostname
    const domainParts = hostname.split('.')

    // Keep only the last two parts (domain.tld) for root domain
    // For localhost or IP addresses, use the full hostname
    let domain
    if (domainParts.length > 2 && !hostname.match(/^\d+\.\d+\.\d+\.\d+$/) && hostname !== 'localhost') {
      domain = '.' + domainParts.slice(-2).join('.')
    } else {
      domain = hostname
    }

    const cookieOptions = {
      expires: 14, // 14 days
      domain: domain,
      path: '/',
      secure: window.location.protocol === 'https:',
      sameSite: 'strict'
    }

    // Set token and timestamp as separate cookies
    Cookies.set('_carecloud_token', token, cookieOptions)
    Cookies.set('_carecloud_token_expiry', timestamp.toString(), cookieOptions)
  }

  /**
   * Delete CareCloud token cookie
   * @returns {boolean} - True if cookie was deleted successfully
   */
  deleteCareCloudToken() {
    // Get the root domain by removing all subdomains
    const hostname = window.location.hostname
    const domainParts = hostname.split('.')

    // Keep only the last two parts (domain.tld) for root domain
    // For localhost or IP addresses, use the full hostname
    let domain
    if (domainParts.length > 2 && !hostname.match(/^\d+\.\d+\.\d+\.\d+$/) && hostname !== 'localhost') {
      domain = '.' + domainParts.slice(-2).join('.')
    } else {
      domain = hostname
    }

    const removeOptions = {
      domain: domain,
      path: '/'
    }

    const fallbackOptions = {
      path: '/'
    }

    // Remove both token and timestamp cookies
    Cookies.remove('_carecloud_token', removeOptions)
    Cookies.remove('_carecloud_token_expiry', removeOptions)

    // Also try to remove without domain specification as fallback
    Cookies.remove('_carecloud_token', fallbackOptions)
    Cookies.remove('_carecloud_token_expiry', fallbackOptions)

    // Verify cookies were deleted
    return !Cookies.get('_carecloud_token') && !Cookies.get('_carecloud_token_expiry')
  }
}